import { getAuth } from 'firebase/auth';

export const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:3800";

/**
 * Makes an authenticated request to the backend API.
 * Automatically includes the Firebase ID token in the Authorization header.
 *
 * @param {string} endpoint - The API endpoint (e.g., '/users/profile').
 * @param {object} options - Optional fetch options (method, body, etc.).
 * @returns {Promise<any>} - The parsed JSON response data.
 * @throws {Error} - Throws an error if the user is not authenticated,
 *                   the request fails, or the response status is not ok.
 */
export const makeAuthenticatedRequest = async (endpoint, options = {}) => {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error("User not authenticated.");
  }

  let idToken;
  try {
    idToken = await user.getIdToken();
  } catch (error) {
    console.error("Error getting ID token:", error);
    throw new Error("Could not verify user identity. Please log in again.");
  }

  const url = `${BASE_URL}${endpoint}`; // Ensure endpoint starts with '/'

  const fetchOptions = {
    ...options, // Spread user-provided options (method, body, etc.)
    headers: {
      ...options.headers, // Spread existing headers
      'Authorization': `Bearer ${idToken}`,
    },
  };

  // Only set Content-Type and stringify body for non-FormData requests
  if (options.body && !(options.body instanceof FormData)) {
    fetchOptions.headers['Content-Type'] = 'application/json';
    if (typeof options.body === 'object') {
      fetchOptions.body = JSON.stringify(options.body);
    }
  }

  let response;
  try {
    response = await fetch(url, fetchOptions);
  } catch (networkError) {
    console.error(`Network error calling ${endpoint}:`, networkError);
    throw new Error(`Network error: ${networkError.message}`);
  }

  let responseData;
  try {
    // Handle cases with no content (e.g., 204 No Content)
    if (response.status === 204) {
      return null; // Or return an empty object/success indicator as needed
    }
    // Check if response is JSON before parsing
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.indexOf("application/json") !== -1) {
        responseData = await response.json();
    } else {
        // Handle non-JSON responses if necessary, e.g., plain text
        responseData = await response.text();
        // If a non-JSON response is not expected on success, treat it as an error
        if (response.ok) {
            console.warn(`Received non-JSON response from ${endpoint}:`, responseData);
            // Depending on API design, you might return the text or throw an error
            return responseData; // Or throw new Error('Unexpected response format');
        }
    }
  } catch (parseError) {
    console.error(`Error parsing JSON response from ${endpoint}:`, parseError);
    // Include response status if available
    const statusText = response ? ` (Status: ${response.status})` : '';
    throw new Error(`Failed to parse server response${statusText}.`);
  }

  if (!response.ok) {
    // Use the message from the parsed JSON error response if available, otherwise use status text
    const errorMessage = responseData?.message || responseData || response.statusText || `HTTP error ${response.status}`;
    console.error(`API error from ${endpoint} (${response.status}):`, errorMessage, responseData);
    // Attach response data to the error object if needed
    const error = new Error(errorMessage);
    error.response = { status: response.status, data: responseData };
    throw error;
  }

  // For successful responses, often the actual data is nested (e.g., { status: 'success', data: {...} })
  // Adjust this based on your backend's common response structure.
  // If the backend sends { status: 'success', data: {...} }, return responseData.data
  // If it just sends the data directly, return responseData
  return responseData.data || responseData; // Adjust as needed
};
